import React from 'react'
import PropTypes from 'prop-types'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import PillToggle from '../../components/shared/PillToggle'
import { COUNTRIES } from '../../helpers/countries'
import {
    holidayIdError,
    MAX_COMMS_DAYS,
    normaliseHolidayId,
} from '../../helpers/holidays'
import { istIsoDate, nowUnix } from '../../utils/datetime'
import { TEXT } from '../../theme/tokens'

const COUNTRY_CODES = Object.keys(COUNTRIES)
const countryLabel = (code) =>
    COUNTRIES[code] ? `${COUNTRIES[code]} (${code})` : code

const onlyDigits = (value) => value.replace(/[^0-9]/g, '')

/**
 * The holiday fields, shared by Create Holiday and the edit dialog.
 *
 * Holds no state: `form` comes from the parent and every change is reported
 * through `onChange(patch)`. All times are IST, which is how the holidays
 * job and every stored holiday read them.
 */
const HolidayForm = ({ form, onChange, creating = false }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
        }}
    >
        <TextField
            id="holiday-name"
            label="Holiday name"
            value={form.holiday_name}
            onChange={(e) => onChange({ holiday_name: e.target.value })}
            placeholder="e.g. Diwali"
            required
            size="small"
        />
        <TextField
            id="holiday-id"
            label="Holiday ID"
            value={form.holiday_id}
            onChange={(e) =>
                onChange({ holiday_id: normaliseHolidayId(e.target.value) })
            }
            placeholder="e.g. diwali_2026"
            // The ID is its pipeline's event type: whitespace inside (a
            // pasted "new year") is refused under the field.
            error={creating && !!holidayIdError(form.holiday_id)}
            helperText={
                creating
                    ? holidayIdError(form.holiday_id) ||
                      'Also the event type of its pipeline. Lower case, cannot be changed later.'
                    : 'Cannot be changed.'
            }
            disabled={!creating}
            required={creating}
            size="small"
        />
        <TextField
            id="holiday-date"
            label="Date of the holiday"
            type="date"
            value={form.holiday_date}
            onChange={(e) => onChange({ holiday_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            // A new holiday cannot be in the past (India-time today). An
            // edit may keep a stored date, so it has no minimum.
            inputProps={creating ? { min: istIsoDate(nowUnix()) } : undefined}
            required
            size="small"
        />
        <TextField
            id="holiday-comms-time"
            label="Comms time (IST)"
            type="time"
            value={form.comms_time}
            onChange={(e) => onChange({ comms_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="From 07:00 onwards."
            required
            size="small"
        />
        <TextField
            id="holiday-comms-days"
            label="Days before the holiday to send the comms"
            value={form.comms_days}
            onChange={(e) =>
                onChange({ comms_days: onlyDigits(e.target.value) })
            }
            inputProps={{ inputMode: 'numeric' }}
            helperText={`1 to ${MAX_COMMS_DAYS} days.`}
            required
            size="small"
        />
        <Autocomplete
            multiple
            id="holiday-countries"
            options={COUNTRY_CODES}
            value={form.allowed_countries}
            onChange={(_, value) => onChange({ allowed_countries: value })}
            getOptionLabel={countryLabel}
            filterSelectedOptions
            size="small"
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Allowed countries"
                    helperText="Leave empty to send to every country."
                />
            )}
        />
        <Box sx={{ gridColumn: '1 / -1' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PillToggle
                    checked={!!form.reminder_on}
                    onChange={(value) => onChange({ reminder_on: value })}
                    label="Send a reminder"
                />
                <Typography sx={{ typography: 'body2', color: TEXT.secondary }}>
                    Send a reminder closer to the holiday
                </Typography>
            </Box>
            {form.reminder_on && (
                <TextField
                    id="holiday-reminder-days"
                    label="Days before the holiday to send the reminder"
                    value={form.reminder_days}
                    onChange={(e) =>
                        onChange({ reminder_days: onlyDigits(e.target.value) })
                    }
                    inputProps={{ inputMode: 'numeric' }}
                    helperText="Fewer days than the comms; same time of day."
                    size="small"
                    sx={{ mt: 2, width: { xs: '100%', md: '50%' } }}
                />
            )}
        </Box>
    </Box>
)

HolidayForm.propTypes = {
    form: PropTypes.shape({
        holiday_id: PropTypes.string,
        holiday_name: PropTypes.string,
        holiday_date: PropTypes.string,
        comms_days: PropTypes.string,
        comms_time: PropTypes.string,
        reminder_on: PropTypes.bool,
        reminder_days: PropTypes.string,
        allowed_countries: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    creating: PropTypes.bool,
}

export default HolidayForm
